// R74 — THE LINE REFERENCE WAS NOT MISSING, IT WAS INVISIBLE, AND THE GUARD THAT LOOKED LIKE
// PROTECTION IS WHAT ADMITTED IT.
//
// `/api/astrocartography/line-meanings` is the free, static, browsable half of the astrocartography
// room: what every planet does on every angle. The read was
// `r.ok ? r.json() : { planets: [] }` → `if (Array.isArray(d.planets)) setMeanings(d.planets)`, and
// **the fabricated fallback IS an array**, so it sailed straight through its own guard and set the
// list to empty. That is R55's finding on `/api/videos/progress` word for word, and the render is
// `meanings.length ? <section> : null` — R58's absent section: the whole reference band, its heading
// and its lede are simply NOT THERE, over a `useState([])` that already looked exactly like that.
//
// **THE SERVER WAS ALREADY HONEST AND HAD NEVER ONCE BEEN HEARD.** `handleAcgLineMeanings` answers
// `200 { ok: false, message: "The line reference is not available right now." }` when the vendor
// table cannot be reached, and `d.ok` was read by nobody — R51/R60/R66's shape, a real sentence with
// no way to a member's screen. **So no server change was made here** (R66's precedent: the honest
// side of a crossing is left alone).
//
// **AND THE SENTENCE THE ROOM DRAWS IS THE CATALOG'S, NOT THE SERVER'S**, which is 14-B's line and
// not a preference: this room turns into Spanish, the server's `message` is English server copy, and
// re-translating a server-composed sentence on the client is a second answer key (14-B3's seam). The
// server's own word still matters — it is what tells the transport the read did not land.
//
// **THE ANSWER IS `T[] | null`, NOT THE FOUR-WAY ONE, AND IT IS ARGUED (R58's ruling):** the handler
// reads no session — the reference is free and carries nothing of hers — so 401 is not an answer it
// can give, and a `sign-in` branch would be a second answer key.
//
// `doFetch` is a PARAMETER (R46), so both answers are reachable with no browser and no vendor.

// The payload's shape, beside the transport that reads it (R73's currency).
export type AcgLineMeaningPlanet = {
  planet: string;
  lines: { lineType: string; angle: string; meaning: string; description: string; keywords: string[]; strength: string; lifeAreas: string[] }[];
};

export const lineMeaningsPath = "/api/astrocartography/line-meanings";

// `null` is "we could not read the reference". An empty array from a landed read is a different
// fact and is kept apart from it (R18), even though both draw no rows: one of them has a sentence.
export async function readLineMeanings(
  doFetch: typeof fetch = typeof fetch === "function" ? fetch : (undefined as never),
): Promise<AcgLineMeaningPlanet[] | null> {
  let response: Response;
  try {
    response = await doFetch(lineMeaningsPath);
  } catch {
    return null;
  }
  const data = await response.json().catch(() => null) as
    | { ok?: boolean; planets?: unknown }
    | null;
  // The server's `ok: false` is an ANSWER and it means the reference is unreachable (R18's seal-status
  // distinction); PRESENCE of the array is what makes the rest a reference (R53).
  if (!response.ok || !data || data.ok === false || !Array.isArray(data.planets)) return null;
  return data.planets as AcgLineMeaningPlanet[];
}

// Three values, because two cannot say it (W5-2/R39): in flight says nothing, a landed read says
// nothing, only a failed one speaks.
export function lineMeaningsNote(answer: AcgLineMeaningPlanet[] | null | undefined, failed: string) {
  return answer === null ? failed : "";
}
